import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useCurrency } from "../../../context/CurrencyContext";
import { getPackagesFromDB } from "../../../services/pricingService";
import logo from '../../../assets/images/logo.png';
import "./PackageDetails.css";

const PackageDetails = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const { selectedCurrency, convertPrice, isLoading: currencyLoading } = useCurrency();

  useEffect(() => {
    fetchPackageDetails();
  }, [packageId, selectedCurrency]);

  const fetchPackageDetails = async () => {
    try {
      setLoading(true);
      const packages = await getPackagesFromDB();
      const packageItem = packages.find(pkg => pkg.key === packageId || pkg.id === packageId);
      
      if (!packageItem) {
        throw new Error('Package not found');
      }
      
      setPackageData(packageItem);
    } catch (error) {
      console.error('Error fetching package details:', error);
      setError('Package not found or failed to load');
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    const currencyMap = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹', 
      'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$'
    };
    return currencyMap[currency] || '₹';
  };

  const formatFeatureName = (key) => {
    const nameMap = {
      'services': 'Services',
      'quickChat': 'Quick Chat',
      'bookings': 'Bookings',
      'featuredProfile': 'Featured Profile',
      'profileWebsite': 'Profile Website',
      'brochures': 'Brochures',
      'articles': 'Articles',
      'awards': 'Awards',
      'memberships': 'Memberships',
      'socialPosts': 'Social Posts',
      'socialVideos': 'Social Videos',
      'autoPost': 'Auto Post',
      'hms': 'HMS Integration',
      'featuredDays': 'Featured Days',
      'duration': 'Duration (Days)'
    };
    
    return nameMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const getFeatureIcon = (key) => {
    const iconMap = {
      'services': '🩺',
      'quickChat': '💬',
      'bookings': '📅',
      'featuredProfile': '⭐',
      'profileWebsite': '🌐',
      'brochures': '📋',
      'articles': '📝',
      'awards': '🏆',
      'memberships': '👥',
      'socialPosts': '📱',
      'socialVideos': '🎥',
      'autoPost': '🤖',
      'hms': '🏥',
      'featuredDays': '📆',
      'duration': '⏰'
    };
    return iconMap[key] || '✨';
  };

  const handleChoosePlan = () => {
    // Handle plan selection - you can add payment integration here
    console.log('Selected package:', packageData);
    // For now, just show an alert
    alert(`Thank you for choosing ${packageData.name} plan! We'll contact you soon.`);
  };

  if (loading) {
    return (
      <div className="package-details-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading package details...</p>
        </div>
      </div>
    );
  }

  if (error || !packageData) {
    return (
      <div className="package-details-error">
        <div className="error-content">
          <h2>Package Not Found</h2>
          <p>{error || 'The package you are looking for does not exist.'}</p>
          <Link to="/" className="back-button">Back to Home</Link>
        </div>
      </div>
    );
  }

  const features = Object.entries(packageData.features);
  const displayedFeatures = showAllFeatures ? features : features.slice(0, 5);

  return (
    <div className="package-details-page">
      {/* Header */}
      <div className="package-details-header">
        <div className="container">
          <Link to="/" className="back-link">
            <i className="fas fa-arrow-left"></i>
            Back to Packages
          </Link>
          <h1>{packageData.name} Package Details</h1>
          <p>Complete information about the {packageData.name} plan</p>
        </div>
      </div>

      <div className="container">
        <div className="package-details-content">
          {/* Package Overview Card */}
          <div className="package-overview-card">
            <div className="package-header-section">
              <div className="package-title">
                <h2>{packageData.name}</h2>
                <span className="package-badge">Active Plan</span>
              </div>
              <div className="package-pricing">
                <div className="price-display">
                  <span className="currency">{getCurrencySymbol(packageData.baseCurrency)}</span>
                  <span className="amount">{packageData.price}</span>
                  <span className="period">/{packageData.period}</span>
                </div>
                {selectedCurrency !== packageData.baseCurrency && (
                  <div className="converted-price">
                    Prices shown in {selectedCurrency}
                    {currencyLoading && <span className="loading-indicator">🔄</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="package-summary">
              <p>Perfect for {packageData.name.toLowerCase()} practices looking to enhance their online presence and patient engagement.</p>
            </div>
          </div>

          {/* Features Section */}
          <div className="features-section">
            <div className="section-header">
              <h3>What's Included</h3>
              <p>All features and benefits of the {packageData.name} plan</p>
            </div>

            <div className="features-grid">
              {displayedFeatures.map(([key, value]) => (
                <div key={key} className="feature-card">
                  <div className="feature-icon">
                    {getFeatureIcon(key)}
                  </div>
                  <div className="feature-content">
                    <h4>{formatFeatureName(key)}</h4>
                    <div className="feature-value">
                      {typeof value === 'boolean' ? (
                        <span className={`status ${value ? 'included' : 'not-included'}`}>
                          {value ? '✓ Included' : '✗ Not Included'}
                        </span>
                      ) : (
                        <span className="numeric-value">{value}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {features.length > 5 && (
              <div className="show-more-section">
                <button 
                  className="show-more-button"
                  onClick={() => setShowAllFeatures(!showAllFeatures)}
                >
                  {showAllFeatures ? 'Show Less' : `Show ${features.length - 5} More Features`}
                  <i className={`fas fa-chevron-${showAllFeatures ? 'up' : 'down'}`}></i>
                </button>
              </div>
            )}
          </div>

          {/* Benefits Section */}
          <div className="benefits-section">
            <div className="section-header">
              <h3>Key Benefits</h3>
              <p>Why choose the {packageData.name} plan?</p>
            </div>

            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">🚀</div>
                <h4>Enhanced Visibility</h4>
                <p>Get more patients to discover your practice through our platform</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">📱</div>
                <h4>Modern Tools</h4>
                <p>Access to the latest digital tools for patient management</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">💼</div>
                <h4>Professional Growth</h4>
                <p>Build your professional reputation and expand your practice</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🎯</div>
                <h4>Targeted Reach</h4>
                <p>Reach the right patients in your area and specialty</p>
              </div>
            </div>
          </div>

          {/* Why Choose MeddyList Section */}
          <div className="why-choose-section">
            <div className="why-choose-container">
              {/* Main Title Circle */}
              <div className="main-title-circle">
                <div className="title-content">
                  <h3>WHY CHOOSE</h3>
                  <h3>MEDDYLIST</h3>
                </div>
              </div>

              {/* Central Hub Circle */}
              <div className="central-hub">
                <div className="hub-content">
                <div className="logo-section">
                  <img src={logo} alt="MeddyList Logo" className="meddylist-logo" />
                </div>

                  <p>Join 10,000+ healthcare professionals who trust our platform to grow their practice and reach more patients.</p>
                  <p>Experience proven results with 300% increase in patient inquiries and 150% growth in online visibility.</p>
                </div>
              </div>

              {/* Feature Points */}
              <div className="feature-points">
                <div className="feature-point top">
                  <div className="feature-circle purple">
                    <i className="fas fa-gem"></i>
                  </div>
                  <div className="feature-text">
                    <h4>Premium Quality</h4>
                    <p>Enterprise-grade platform with cutting-edge features designed specifically for healthcare professionals.</p>
                  </div>
                </div>

                <div className="feature-point middle-right">
                  <div className="feature-circle blue">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="feature-text">
                    <h4>24/7 Support</h4>
                    <p>Round-the-clock customer support to ensure your practice runs smoothly at all times.</p>
                  </div>
                </div>

                <div className="feature-point lower-right">
                  <div className="feature-circle teal">
                    <i className="fas fa-hand-pointer"></i>
                  </div>
                  <div className="feature-text">
                    <h4>Easy Integration</h4>
                    <p>Seamless integration with your existing systems and workflows for maximum efficiency.</p>
                  </div>
                </div>

                <div className="feature-point bottom">
                  <div className="feature-circle grey">
                    <i className="fas fa-heart"></i>
                  </div>
                  <div className="feature-text">
                    <h4>Patient-Centric</h4>
                    <p>Built with patients in mind, ensuring better engagement and improved healthcare outcomes.</p>
                  </div>
                </div>
              </div>

              {/* Connecting Lines */}
              <div className="connecting-lines">
                <div className="line line-1"></div>
                <div className="line line-2"></div>
                <div className="line line-3"></div>
                <div className="line line-4"></div>
              </div>
            </div>
          </div>

          {/* Comparison Section */}
          <div className="comparison-section">
            <div className="section-header">
              <h3>Plan Comparison</h3>
              <p>See how the {packageData.name} plan compares to others</p>
            </div>

            <div className="comparison-table">
              <div className="table-header">
                <div className="feature-column">Feature</div>
                <div className="plan-column">Free</div>
                <div className="plan-column">Basic</div>
                <div className="plan-column active">Business</div>
              </div>
              <div className="table-body">
                <div className="table-row">
                  <div className="feature-name">Services</div>
                  <div className="feature-value">5</div>
                  <div className="feature-value">15</div>
                  <div className="feature-value active">30</div>
                </div>
                <div className="table-row">
                  <div className="feature-name">Quick Chat</div>
                  <div className="feature-value">✗</div>
                  <div className="feature-value">✓</div>
                  <div className="feature-value active">✓</div>
                </div>
                <div className="table-row">
                  <div className="feature-name">Bookings</div>
                  <div className="feature-value">✗</div>
                  <div className="feature-value">✓</div>
                  <div className="feature-value active">✓</div>
                </div>
                <div className="table-row">
                  <div className="feature-name">Featured Profile</div>
                  <div className="feature-value">✗</div>
                  <div className="feature-value">✗</div>
                  <div className="feature-value active">✓</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="cta-section">
            <div className="cta-content">
              <h3>Ready to Get Started?</h3>
              <p>Join thousands of healthcare professionals who trust our platform</p>
              <div className="cta-buttons">
                <button className="primary-cta" onClick={handleChoosePlan}>
                  Choose {packageData.name} Plan
                </button>
                <Link to="/" className="secondary-cta">
                  View All Packages
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageDetails;
